import { useState, useRef, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CloseRounded,
  HistoryRounded,
  SendRounded,
  AutoAwesomeRounded,
  DeleteOutlineRounded,
  AddCommentOutlined,
  MenuBookOutlined,
  StorageOutlined,
  OpenInNewRounded,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import type { AssistantChatSessionProps, AssistantChatSummaryProps } from '@dosfilos/domain';
import { assistantApi } from '../../lib/api/assistant.api';
import {
  assistantHintFromPath,
  assistantContextLabelKey,
  isSameContext,
} from '../../lib/assistant-context';
import { errorText } from '../../lib/error-text';
import { formatDateTime, formatTime } from '../../lib/i18n-format';
import { useTenantStore } from '../../stores/tenant.store';
import { useConfirm } from '../ConfirmProvider';
import { routeLabel } from '@dosfilos/shared';

// ─── El asistente, disponible en cualquier pantalla ──────────────────────────
//
// TRANSVERSAL (decisión del director, byblos `01M33CYJWB27F2BHCVJG2CBYXM`).
// Entender una sigla o por qué se pide algo surge MIENTRAS se hace una tarea; que
// la persona tenga que estar en la pantalla correcta para preguntar es al revés.
//
// NACE CERRADO Y RECUERDA SU ESTADO (opción del director). Abierto de entrada se
// come un tercio de la pantalla en tablas anchas como la matriz de alineación;
// cerrado y sin memoria nadie lo descubre dos veces. Recordarlo lo hace
// descubrible una vez y obediente después.
//
// UNA CONVERSACIÓN POR CONTEXTO, CON HISTORIAL. Preguntar por dos rondas en el
// mismo hilo confunde al modelo, que reenvía la historia completa. El historial
// resuelve el costo de esa separación: se puede volver a cualquiera.

const OPEN_KEY = 'assistant.drawer.open';

// 400 quedaba angosto: las respuestas son de varios párrafos y los chips de cita
// llevan el nombre completo del documento, que se truncaba casi siempre.
//
// `clamp` y no `min`: el cajón toma este ancho desde `sm` (600px), y un `min`
// con `42vw` habría dado 322px en una pantalla de 768 — MÁS ANGOSTO que los 400
// que había antes. El piso conserva lo que ya funcionaba; el techo evita que en
// una pantalla muy ancha el panel se coma la página.
const WIDTH = 'clamp(400px, 42vw, 520px)';

// LA PREGUNTA Y LA RESPUESTA SE DISTINGUEN POR FORMA, no sólo por color. Antes
// eran dos grises del tema —`action.selected` y `action.hover`— que en oscuro
// quedan casi iguales, así que el hilo se leía como un bloque monótono y había
// que fijarse en la alineación para saber quién habló.
//
// La pregunta es un bloque compacto y teñido, con la esquina de abajo a la
// derecha recta: apunta a quien escribió. La respuesta ocupa el ancho, tiene
// borde y la esquina contraria recta. Ninguna depende del color para leerse.
const askSx = {
  maxWidth: '88%',
  px: 1.75,
  py: 1.25,
  borderRadius: '14px 14px 4px 14px',
  bgcolor: 'primary.main',
  color: 'primary.contrastText',
} as const;

const answerSx = {
  maxWidth: '100%',
  px: 1.75,
  py: 1.5,
  borderRadius: '14px 14px 14px 4px',
  bgcolor: 'background.paper',
  border: '1px solid',
  borderColor: 'divider',
} as const;

export function AssistantDrawer() {
  const { t, i18n } = useTranslation('sis');
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const confirm = useConfirm();

  const [open, setOpen] = useState(false);
  const [session, setSession] = useState<AssistantChatSessionProps | null>(null);
  const [history, setHistory] = useState<AssistantChatSummaryProps[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [openingChat, setOpeningChat] = useState(false);
  const [historyAnchor, setHistoryAnchor] = useState<HTMLElement | null>(null);
  const [draft, setDraft] = useState('');
  // La pregunta recién enviada, mientras el servidor responde. Sin esto la
  // persona escribe, presiona enviar, y la pantalla no acusa recibo hasta que
  // llega la respuesta entera: parece que se perdió.
  const [pending, setPending] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const endRef = useRef<HTMLDivElement>(null);

  // Suscrito al store: si la persona cambia de organización con el panel
  // abierto, el hilo se recarga solo para el tenant nuevo en vez de seguir
  // mostrando el de antes.
  const tenantId = useTenantStore((s) => s.currentTenantId);

  const hint = assistantHintFromPath(pathname);

  /** Qué dice el chip. Con conversación abierta, DE QUÉ ES ELLA —su nombre si lo
   *  tiene, si no la etiqueta genérica—. Sin conversación, de qué SERÁ la que
   *  empiece acá, que es el único caso en que la pantalla decide. */
  const threadLabel = session
    ? (session.contextLabel ?? t(assistantContextLabelKey(session.context.kind)))
    : t(assistantContextLabelKey(hint?.kind ?? 'tmai_process'));

  // Recupera el estado del cajón. `localStorage` puede lanzar (ventana privada,
  // datos de sitio bloqueados), así que nunca se lee sin protección.
  useEffect(() => {
    try {
      setOpen(window.localStorage.getItem(OPEN_KEY) === '1');
    } catch {
      /* sin memoria: nace cerrado, que es el default elegido */
    }
  }, []);

  const toggle = (next: boolean) => {
    setOpen(next);
    try {
      window.localStorage.setItem(OPEN_KEY, next ? '1' : '0');
    } catch {
      /* no poder recordarlo no impide usarlo */
    }
  };

  /** La conversación del contexto actual. Al cambiar de pantalla cambia el hilo:
   *  es lo que evita que dos expedientes se mezclen en la misma historia. */
  const loadForContext = useCallback(async () => {
    if (!tenantId || !open) return;
    setError('');
    try {
      const { sessions } = await assistantApi.list({
        tenantId,
        ...(hint ? { contextHint: hint } : {}),
      });
      setSession(sessions[0] ?? null);
    } catch (e) {
      setError(errorText(e));
    }
  }, [tenantId, open, hint?.kind, hint?.id]);

  useEffect(() => {
    void loadForContext();
  }, [loadForContext]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [session?.turns.length, open, pending, busy]);

  const send = async () => {
    const message = draft.trim();
    if (!message || busy || !tenantId) return;
    setBusy(true);
    setError('');
    setDraft('');
    setPending(message);
    try {
      setSession(
        await assistantApi.send({
          tenantId,
          message,
          ...(hint ? { contextHint: hint } : {}),
          ...(session ? { sessionId: session.id } : {}),
          uiLocale: i18n.language.startsWith('en') ? 'en' : 'es',
        }),
      );
    } catch (e) {
      setError(errorText(e));
      // EL CAMPO ES EL ÚNICO LUGAR DONDE SOBREVIVE LA PREGUNTA. El servidor ya
      // no guarda un intento que falló —dejaba turnos sin respuesta en el hilo
      // y conversaciones fantasma en el historial—, así que devolverla acá no
      // es una comodidad: es lo que evita que la persona la pierda.
      setDraft(message);
    } finally {
      // Se limpia SIEMPRE: si salió bien, el turno ya viene en la sesión y
      // dejarlo duplicaría la pregunta; si salió mal, el texto volvió al campo.
      setPending('');
      setBusy(false);
    }
  };

  /** Eliminar una conversación. BORRA DE VERDAD, por eso se confirma.
   *
   *  Si la borrada es la que está abierta, el panel vuelve a empezar en blanco:
   *  dejar los turnos en pantalla después de borrarlos mostraría algo que ya no
   *  existe, y el siguiente mensaje intentaría continuar una sesión muerta. */
  const removeChat = async (id: string) => {
    if (!tenantId) return;
    const ok = await confirm({
      title: t('assistant.delete.title'),
      message: t('assistant.delete.message'),
      confirmColor: 'error',
    });
    if (!ok) return;
    setHistoryAnchor(null);
    try {
      await assistantApi.remove({ tenantId, sessionId: id });
      setHistory((prev) => prev.filter((s) => s.id !== id));
      if (session?.id === id) setSession(null);
    } catch (e) {
      setError(errorText(e));
    }
  };

  /** Traer la conversación elegida. La lista sólo tiene su resumen.
   *
   *  Se limpia el hilo ANTES de pedirla: dejar el anterior en pantalla mientras
   *  llega el nuevo muestra una conversación que ya no es la que se eligió. */
  const openChat = async (id: string) => {
    if (!tenantId) return;
    setSession(null);
    // ESTADO PROPIO, NO `busy`. Reusar `busy` mostraba «Pensando…», que dice
    // que el modelo está respondiendo cuando nadie preguntó nada. Y con la
    // sesión ya vacía, el panel afirmaba además «aún no has preguntado nada»
    // mientras cargaba — el MISMO defecto que el historial tenía: declarar
    // vacío lo que todavía no se sabe.
    setOpeningChat(true);
    setError('');
    try {
      setSession(await assistantApi.open({ tenantId, sessionId: id }));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setOpeningChat(false);
    }
  };

  /** Empezar de cero. NACE DE LA PANTALLA DONDE ESTÁS: es el único momento en
   *  que la ruta decide el contexto de una conversación.
   *
   *  Faltaba por completo, y el hueco mordía dos veces: al querer separar temas,
   *  y al llegar al tope de turnos, donde el aviso dice «empieza una nueva» y no
   *  había ninguna manera de hacerlo. */
  const startNew = () => {
    // Con un mensaje en vuelo no se empieza: la respuesta que llega haría
    // `setSession(...)` y resucitaría la conversación que se acaba de dejar.
    if (busy) return;
    setSession(null);
    setHistoryAnchor(null);
    setError('');
    setDraft('');
  };

  /** Abrir el historial.
   *
   *  SE ABRE CARGANDO, NO VACÍO. Antes se abría el menú y la carga venía
   *  después, así que durante un segundo decía «no hay conversaciones» —una
   *  afirmación falsa— y recién entonces aparecían. Quien lo veía concluía que
   *  había perdido su historial, que es lo peor que puede decir una pantalla
   *  mientras espera. */
  const openHistory = async (el: HTMLElement) => {
    setHistoryAnchor(el);
    if (!tenantId) return;
    setHistoryLoading(true);
    try {
      const { sessions } = await assistantApi.history({ tenantId });
      setHistory(sessions);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  if (!tenantId) return null;

  return (
    <>
      <Tooltip title={t('assistant.open')}>
        <IconButton onClick={() => toggle(!open)} aria-label={t('assistant.open')}>
          <AutoAwesomeRounded />
        </IconButton>
      </Tooltip>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => toggle(false)}
        // `keepMounted` para no perder el borrador al cerrar y volver a abrir.
        ModalProps={{ keepMounted: true }}
        PaperProps={{ sx: { width: { xs: '100%', sm: WIDTH } } }}
      >
        <Stack sx={{ height: '100%' }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}
          >
            <AutoAwesomeRounded fontSize="small" color="action" />
            <Typography variant="subtitle2" sx={{ flex: 1, minWidth: 0 }}>
              {t('assistant.title')}
            </Typography>
            <Tooltip title={t('assistant.new')}>
              <IconButton
                size="small"
                onClick={startNew}
                disabled={busy}
                aria-label={t('assistant.new')}
              >
                <AddCommentOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('assistant.history')}>
              <IconButton size="small" onClick={(e) => void openHistory(e.currentTarget)}>
                <HistoryRounded fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton
              size="small"
              onClick={() => toggle(false)}
              aria-label={t('assistant.close')}
            >
              <CloseRounded fontSize="small" />
            </IconButton>
          </Stack>

          {/* DE QUÉ ES ESTA CONVERSACIÓN, no dónde estás parado.
              Antes este chip se armaba con la RUTA, así que mostraba «Esta ronda
              de propuesta» encima de una conversación general. El panel afirmaba
              algo falso y después rechazaba el mensaje por creerle. */}
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            alignItems="center"
            sx={{ px: 2, pt: 1.5 }}
          >
            <Chip size="small" variant="outlined" label={threadLabel} sx={{ maxWidth: '100%' }} />
            {/* Estando en un expediente con otra conversación abierta, se OFRECE
                abrir una sobre este. Ofrecer, no bloquear: antes el panel
                rechazaba el mensaje y no decía siquiera a qué pantalla ir. */}
            {hint && session && !isSameContext(session.context, hint) && (
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                clickable
                onClick={startNew}
                label={t('assistant.askAboutThis')}
              />
            )}
          </Stack>

          <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1.5 }}>
            {/* «Aún no has preguntado nada» SÓLO cuando de verdad no hay nada:
                ni una pregunta en vuelo, ni una conversación viniendo. */}
            {!session?.turns.length && !pending && !openingChat && (
              <Typography variant="body2" color="text.secondary">
                {t('assistant.empty')}
              </Typography>
            )}

            {openingChat && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1 }}>
                <CircularProgress size={14} />
                <Typography variant="body2" color="text.secondary">
                  {t('assistant.openingChat')}
                </Typography>
              </Stack>
            )}

            <Stack spacing={1.5}>
              {session?.turns.map((turn) => (
                <Box
                  key={turn.index}
                  sx={{
                    ...(turn.role === 'user' ? askSx : answerSx),
                    alignSelf: turn.role === 'user' ? 'flex-end' : 'flex-start',
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {turn.text}
                  </Typography>

                  {/* A DÓNDE IR. Explicar qué lleva un formulario sin decir
                      dónde está deja a alguien sabiendo qué hacer y sin poder
                      hacerlo. Al navegar se cierra el panel: la persona quería
                      ir a esa pantalla, no seguir leyendo sobre ella. */}
                  {turn.navigateTo && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<OpenInNewRounded fontSize="small" />}
                      onClick={() => {
                        toggle(false);
                        navigate(turn.navigateTo!);
                      }}
                      sx={{ mt: 1.5, textTransform: 'none' }}
                    >
                      {t('assistant.goToScreen', { screen: routeLabel(turn.navigateTo) })}
                    </Button>
                  )}

                  {turn.outOfCorpus && (
                    <Chip
                      size="small"
                      variant="outlined"
                      color="warning"
                      sx={{ mt: 1 }}
                      label={t('assistant.outOfCorpus')}
                    />
                  )}

                  {/* Las citas se muestran SIEMPRE que las haya: un corpus
                      cerrado sin citas visibles es indistinguible de uno
                      abierto. El valor no es que el modelo se limite, sino que
                      se pueda comprobar que se limitó.
                      
                      PERO SON SUBORDINADAS, no otro mensaje. Como chips
                      competían con la respuesta y se truncaban a la mitad; acá
                      van bajo una línea, en texto chico, con el nombre completo
                      y la sección en su propia línea. Se leen si se buscan y no
                      estorban si no. */}
                  {turn.citations?.length || turn.queries?.length ? (
                    <Box
                      sx={{
                        mt: 1.5,
                        pt: 1,
                        borderTop: '1px dashed',
                        borderColor: 'divider',
                      }}
                    >
                      {turn.citations?.map((c, i) => (
                        <Stack
                          key={`${c.sourceId}-${i}`}
                          direction="row"
                          spacing={0.75}
                          alignItems="flex-start"
                          sx={{ mt: i ? 0.5 : 0 }}
                        >
                          <MenuBookOutlined
                            sx={{ fontSize: 14, mt: '2px', color: 'text.disabled', flexShrink: 0 }}
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ lineHeight: 1.4 }}
                          >
                            {c.sourceLabel}
                            {c.section ? (
                              <Box
                                component="span"
                                sx={{ display: 'block', color: 'text.disabled' }}
                              >
                                {c.section}
                              </Box>
                            ) : null}
                          </Typography>
                        </Stack>
                      ))}

                      {/* LA PROCEDENCIA DE LOS DATOS EN VIVO, con otro icono a
                          propósito: un número que salió de mirar el sistema hoy
                          no tiene la misma clase de respaldo que una frase de un
                          documento, y confundirlos fue el defecto — el modelo
                          citaba el documento de ayuda que más se parecía.

                          LA FIRMA EL SERVIDOR. No es el modelo diciendo qué
                          consultó: es el sistema anotando qué ejecutó. */}
                      {turn.queries?.length ? (
                        <Stack
                          direction="row"
                          spacing={0.75}
                          alignItems="flex-start"
                          sx={{ mt: turn.citations?.length ? 0.5 : 0 }}
                        >
                          <StorageOutlined
                            sx={{ fontSize: 14, mt: '2px', color: 'text.disabled', flexShrink: 0 }}
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ lineHeight: 1.4 }}
                          >
                            {/* La hora pasa por el helper: el idioma lo decide
                                la app, no el navegador. */}
                            {t('assistant.queriedAt', {
                              // SIN SEGUNDOS: es un sello de cuándo se miró el
                              // dato, no un cronómetro. «1:32:57 p. m.» hace
                              // leer tres números donde importan dos.
                              at: formatTime(turn.at, { hour: 'numeric', minute: '2-digit' }),
                            })}
                            <Box component="span" sx={{ display: 'block', color: 'text.disabled' }}>
                              {turn.queries
                                .map((q) => t(`assistant.queries.${q}`, { defaultValue: q }))
                                .join(' · ')}
                            </Box>
                          </Typography>
                        </Stack>
                      ) : null}
                    </Box>
                  ) : null}
                </Box>
              ))}

              {/* La pregunta en vuelo, con el mismo aspecto que tendrá cuando el
                  servidor la devuelva: al llegar la respuesta no hay salto. */}
              {pending && (
                <Box sx={{ ...askSx, alignSelf: 'flex-end' }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {pending}
                  </Typography>
                </Box>
              )}

              {busy && (
                <Box
                  sx={{
                    ...answerSx,
                    alignSelf: 'flex-start',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <CircularProgress size={14} />
                  <Typography variant="body2" color="text.secondary">
                    {t('assistant.thinking')}
                  </Typography>
                </Box>
              )}

              <div ref={endRef} />
            </Stack>

            {error && (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                {error}
              </Alert>
            )}
          </Box>

          <Divider />
          <Stack direction="row" spacing={1} alignItems="flex-end" sx={{ p: 1.5 }}>
            <TextField
              fullWidth
              multiline
              maxRows={5}
              size="small"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={t('assistant.placeholder')}
              disabled={busy}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void send();
                }
              }}
            />
            <Button
              variant="outlined"
              onClick={() => void send()}
              disabled={busy || !draft.trim()}
              sx={{ minWidth: 44, px: 1.5 }}
              aria-label={t('assistant.send')}
            >
              {busy ? <CircularProgress size={16} /> : <SendRounded fontSize="small" />}
            </Button>
          </Stack>
        </Stack>
      </Drawer>

      <Menu
        anchorEl={historyAnchor}
        open={!!historyAnchor}
        onClose={() => setHistoryAnchor(null)}
        slotProps={{ paper: { sx: { maxWidth: 360 } } }}
      >
        {historyLoading && (
          <MenuItem disabled sx={{ gap: 1 }}>
            <CircularProgress size={14} />
            {t('assistant.historyLoading')}
          </MenuItem>
        )}
        {/* «No hay conversaciones» SÓLO cuando de verdad se sabe que no hay. */}
        {!historyLoading && history.length === 0 && (
          <MenuItem disabled>{t('assistant.historyEmpty')}</MenuItem>
        )}
        {history.map((s) => (
          <MenuItem
            key={s.id}
            onClick={() => {
              setHistoryAnchor(null);
              void openChat(s.id);
            }}
            sx={{ gap: 1 }}
          >
            <Stack sx={{ minWidth: 0, flex: 1 }}>
              {/* De qué era, no sólo cuándo: una lista de fechas no sirve para
                  volver a nada. */}
              <Typography variant="body2" noWrap>
                {s.contextLabel ?? t(assistantContextLabelKey(s.context.kind))}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {formatDateTime(s.updatedAt)}
                {s.firstQuestion ? ` · ${s.firstQuestion}` : ''}
              </Typography>
            </Stack>
            {/* `stopPropagation` para que borrar no abra además la conversación
                que se está borrando. */}
            <Tooltip title={t('assistant.delete.action')}>
              <IconButton
                size="small"
                aria-label={t('assistant.delete.action')}
                onClick={(e) => {
                  e.stopPropagation();
                  void removeChat(s.id);
                }}
              >
                <DeleteOutlineRounded fontSize="small" />
              </IconButton>
            </Tooltip>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
